/**
 * Noggin mock-up generator — front-end controller.
 *
 * 8 free designs per session (3 beanies, 2 caps, 3 bucket hats), driven by
 * the customer's uploaded logo plus their two picked colours. No email
 * required for the first free set. Anything beyond that requires an
 * email. This file only handles UI — the backend is the source of truth
 * for the actual limit, since anything checked only here can be bypassed
 * by refreshing the page. See /backend/README.md.
 *
 * This deliberately does NOT rely on native <form> submission. The
 * generate button is type="button" with a direct click listener, not a
 * submit handler — avoids any conflict with other forms elsewhere on the
 * page (cart, search, newsletter).
 */
(function () {
  var root = document.querySelector('[data-mockup-generator]');
  if (!root) return;

  var PRODUCT_GROUPS = [
    { name: 'Beanies', count: 3 },
    { name: 'Caps', count: 2 },
    { name: 'Bucket hats', count: 3 },
  ];

  var endpoint = root.dataset.apiEndpoint;
  var typeformUrl = root.dataset.typeformUrl;
  var fileInput = root.querySelector('#mockup-logo-input');
  var filenameEl = root.querySelector('[data-mockup-filename]');
  var primaryInput = root.querySelector('[data-mockup-primary]');
  var secondaryInput = root.querySelector('[data-mockup-secondary]');
  var swapBtn = root.querySelector('[data-mockup-swap]');
  var resultsEl = root.querySelector('[data-mockup-results]');
  var statusEl = root.querySelector('[data-mockup-status]');
  var submitBtn = root.querySelector('[data-mockup-submit]');
  var emailBtn = root.querySelector('[data-mockup-email-submit]');
  var emailInput = root.querySelector('[data-mockup-email-input]');
  var emailForm = root.querySelector('[data-mockup-email-form]');
  var secondTierNote = root.querySelector('[data-mockup-second-tier]');

  var sessionId = getOrCreateSessionId();

  fileInput.addEventListener('change', function () {
    filenameEl.textContent = fileInput.files[0] ? fileInput.files[0].name : '';
  });

  if (swapBtn && primaryInput && secondaryInput) {
    swapBtn.addEventListener('click', function () {
      var temp = primaryInput.value;
      primaryInput.value = secondaryInput.value;
      secondaryInput.value = temp;
    });
  }

  if (submitBtn) {
    submitBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (!fileInput.files[0]) {
        setStatus('Please choose a logo file first.');
        return;
      }
      generate({
        tier: 1,
        logo: fileInput.files[0],
        primaryColor: primaryInput ? primaryInput.value : '#14170F',
        secondaryColor: secondaryInput ? secondaryInput.value : '#E2FF6E',
      });
    });
  }

  if (emailBtn && emailInput) {
    emailBtn.addEventListener('click', function (e) {
      e.preventDefault();
      var email = emailInput.value;
      if (!email) {
        setStatus('Please enter your email.');
        return;
      }
      generate({
        tier: 2,
        logo: fileInput.files[0],
        primaryColor: primaryInput ? primaryInput.value : '#14170F',
        secondaryColor: secondaryInput ? secondaryInput.value : '#E2FF6E',
        email: email,
      });
    });
  }

  function generate(opts) {
    if (!endpoint) {
      setStatus('Mock-up backend is not configured yet for this section.');
      return;
    }

    setLoading(true);
    setStatus('Reading your logo…');

    var reader = new FileReader();
    reader.onload = function () {
      setStatus('Generating your concepts…');

      var payload = JSON.stringify({
        tier: opts.tier,
        sessionId: sessionId,
        primaryColor: opts.primaryColor,
        secondaryColor: opts.secondaryColor,
        logoBase64: reader.result,
        logoMimeType: opts.logo.type,
        email: opts.email || undefined,
      });

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
      })
        .then(function (res) {
          if (!res.ok) return res.json().then(function (err) { throw err; });
          return res.json();
        })
        .then(function (data) {
          renderResults(data.images || [], data.productTypes || []);
          setStatus(data.message || 'Here are your concepts.');

          if (opts.tier === 1 && secondTierNote) {
            secondTierNote.hidden = true;
            if (emailForm) emailForm.hidden = false;
          }
          if (opts.tier === 2 && emailForm) {
            emailForm.hidden = true;
          }
        })
        .catch(function (err) {
          if (err && err.code === 'LIMIT_REACHED') {
            setStatus(err.message || "You've used your free designs for this session.");
          } else {
            setStatus('Something went wrong generating your mock-ups. Please try again.');
          }
        })
        .finally(function () {
          setLoading(false);
        });
    };
    reader.onerror = function () {
      setLoading(false);
      setStatus('Could not read your logo file. Please try a different file.');
    };
    reader.readAsDataURL(opts.logo);
  }

  function renderResults(images, productTypes) {
    resultsEl.innerHTML = '';
    var index = 0;
    PRODUCT_GROUPS.forEach(function (group) {
      var groupEl = document.createElement('div');
      groupEl.className = 'results-group';

      var label = document.createElement('span');
      label.className = 'results-group-label';
      label.textContent = group.name;
      groupEl.appendChild(label);

      var grid = document.createElement('div');
      grid.className = group.count === 2 ? 'results results--two' : 'results';

      for (var i = 0; i < group.count; i++) {
        var slot = document.createElement('div');
        slot.className = 'result-slot';
        var img = images[index];
        var productType = productTypes[index] || group.name;

        if (img) {
          var imgEl = document.createElement('img');
          imgEl.src = img;
          imgEl.alt = group.name + ' mock-up concept ' + (i + 1);
          imgEl.style.width = '100%';
          imgEl.style.height = '100%';
          imgEl.style.objectFit = 'cover';
          slot.appendChild(imgEl);

          if (typeformUrl) {
            var pickBtn = document.createElement('button');
            pickBtn.type = 'button';
            pickBtn.className = 'result-pick-btn';
            pickBtn.textContent = 'Choose this';
            pickBtn.addEventListener('click', function (chosenImg, chosenType) {
              return function () {
                openOrderForm(chosenImg, chosenType);
              };
            }(img, productType));
            slot.appendChild(pickBtn);
          }
        } else {
          slot.textContent = String(index + 1).padStart(2, '0');
        }
        grid.appendChild(slot);
        index++;
      }

      groupEl.appendChild(grid);
      resultsEl.appendChild(groupEl);
    });
  }

  function openOrderForm(imageUrl, productType) {
    if (!typeformUrl) return;
    var url = new URL(typeformUrl);
    url.searchParams.set('ref', sessionId);
    url.searchParams.set('design_url', imageUrl);
    url.searchParams.set('product_type', productType);
    window.open(url.toString(), '_blank', 'noopener');
  }

  function setLoading(isLoading) {
    if (submitBtn) submitBtn.disabled = isLoading;
    resultsEl.classList.toggle('is-loading', isLoading);
  }

  function setStatus(text) {
    statusEl.textContent = text;
  }

  function getOrCreateSessionId() {
    var key = 'noggin_mockup_session';
    var existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    var id = 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2);
    window.sessionStorage.setItem(key, id);
    return id;
  }
})();
